import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RESOURCE_TYPES, formatEuro } from "@/lib/resources";
import { computeCosts } from "@/lib/costs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Box, Plus, Trash2 } from "lucide-react";

const NEW_UNIT = "__new__";
const NEW_VAT = "__newvat__";
const NO_SUPPLIER = "__none__";
const EMPTY = { name: "", description: "", type: "Material", unit_cost: "", unit: "", reference: "", notes: "", safety_pct: "15", sale_pct: "25", activity_id: "", protocol: "", proof: "" };
const EMPTY_CONTACT = { company: "", contact_person: "", phone: "", email: "" };

export default function ResourceFormModal({ open, onOpenChange, units, contacts, resources, defaultActivityId, defaultType, onSaved, onContactCreated, resource }) {
  const editing = Boolean(resource);
  const [form, setForm] = useState(EMPTY);
  const [kind, setKind] = useState("simple");
  const [components, setComponents] = useState([]);
  const [unitMode, setUnitMode] = useState("");
  const [customUnit, setCustomUnit] = useState("");
  const [vatRates, setVatRates] = useState([21, 10, 0]);
  const [vatMode, setVatMode] = useState("21");
  const [customVat, setCustomVat] = useState("");
  const [supplierId, setSupplierId] = useState(NO_SUPPLIER);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [creatingContact, setCreatingContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingSupplierRef = useRef(null);

  // Promote a just-created contact to selected supplier only once its SelectItem is mounted,
  // avoiding the Radix controlled-Select value/item race.
  useEffect(() => {
    if (pendingSupplierRef.current && (contacts || []).some((c) => c.contact_id === pendingSupplierRef.current)) {
      setSupplierId(pendingSupplierRef.current);
      pendingSupplierRef.current = null;
    }
  }, [contacts]);

  useEffect(() => {
    if (!open) return;
    setShowNewContact(false);
    setNewContact(EMPTY_CONTACT);
    api.get("/vat-rates").then((r) => { if (r.data?.length) setVatRates(r.data); }).catch(() => {});
    if (resource) {
      setForm({
        name: resource.name || "", type: resource.type || "Material",
        description: resource.description || "",
        unit_cost: resource.unit_cost ?? "", unit: resource.unit || "",
        reference: resource.reference || "", notes: resource.notes || "",
        safety_pct: String(resource.safety_margin_pct ?? 15),
        sale_pct: String(resource.sale_margin_pct ?? 25),
        activity_id: resource.activity_id || "",
        protocol: resource.protocol || "",
        proof: resource.proof || "",
      });
      setUnitMode(resource.unit && units.includes(resource.unit) ? resource.unit : (resource.unit ? NEW_UNIT : ""));
      setCustomUnit(resource.unit && !units.includes(resource.unit) ? resource.unit : "");
      setSupplierId(resource.supplier_contact_id || NO_SUPPLIER);
      setVatMode(String(resource.vat_rate ?? 21));
      setCustomVat("");
      setKind(resource.kind || "simple");
      setComponents((resource.components || []).map((c) => ({ resource_id: c.resource_id, quantity: c.quantity ?? 1 })));
    } else {
      setForm({ ...EMPTY, activity_id: defaultActivityId || "", type: defaultType || EMPTY.type });
      setUnitMode("");
      setCustomUnit("");
      setSupplierId(NO_SUPPLIER);
      setVatMode("21");
      setCustomVat("");
      setKind("simple");
      setComponents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resource, units]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isTarea = form.type === "Tarea";

  const simples = (resources || []).filter((r) => r.kind !== "compuesto" && (!resource || r.resource_id !== resource.resource_id));
  const grossOf = (rid) => { const r = (resources || []).find((x) => x.resource_id === rid); return r ? Number(r.unit_cost) : 0; };
  const grossTotal = components.reduce((s, c) => s + grossOf(c.resource_id) * (Number(c.quantity) || 0), 0);
  const baseGross = kind === "compuesto" ? grossTotal : Number(form.unit_cost || 0);
  const vatPct = vatMode === NEW_VAT ? Number(customVat || 0) : Number(vatMode);
  const costs = computeCosts({ gross: baseGross, vat: vatPct, safetyPct: Number(form.safety_pct || 0), salePct: Number(form.sale_pct || 0) });
  const hasCost = kind === "compuesto" ? grossTotal > 0 : form.unit_cost !== "";
  const addComponent = () => setComponents((cs) => [...cs, { resource_id: "", quantity: 1 }]);
  const updateComponent = (i, patch) => setComponents((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeComponent = (i) => setComponents((cs) => cs.filter((_, idx) => idx !== i));

  const createContact = async () => {
    if (!newContact.company.trim()) return toast.error("Indica el nombre de empresa.");
    setCreatingContact(true);
    try {
      const { data } = await api.post("/contacts", {
        company: newContact.company.trim(),
        contact_person: newContact.contact_person.trim() || null,
        phone: newContact.phone.trim() || null,
        email: newContact.email.trim() || null,
        type: "Proveedor",
      });
      onContactCreated?.(data);
      pendingSupplierRef.current = data.contact_id;
      setShowNewContact(false);
      setNewContact(EMPTY_CONTACT);
      toast.success("Contacto creado y asignado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el contacto.");
    } finally {
      setCreatingContact(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    let unit = unitMode === NEW_UNIT ? customUnit.trim() : unitMode;
    if (!form.name.trim()) return toast.error("Indica el nombre del recurso.");
    if (isTarea && !unit) unit = "tarea";
    if (!unit) return toast.error("Indica la unidad de compra.");
    const validComponents = components.filter((c) => c.resource_id);
    if (!isTarea && kind === "simple" && (form.unit_cost === "" || Number(form.unit_cost) < 0))
      return toast.error("Indica un coste válido.");
    if (kind === "compuesto" && validComponents.length === 0)
      return toast.error("Añade al menos un recurso al desglose.");
    const vat = vatMode === NEW_VAT ? Number(customVat) : Number(vatMode);
    if (vatMode === NEW_VAT && (customVat === "" || Number(customVat) < 0 || Number(customVat) > 100))
      return toast.error("Indica un IVA válido (entre 0 y 100).");
    setSaving(true);
    const payload = {
      name: form.name.trim(), type: form.type, unit, vat_rate: vat, kind,
      safety_margin_pct: Number(form.safety_pct || 0),
      sale_margin_pct: Number(form.sale_pct || 0),
      supplier_contact_id: supplierId === NO_SUPPLIER ? null : supplierId,
      reference: form.reference.trim() || null, notes: form.notes.trim() || null,
      description: form.description.trim() || null,
      activity_id: null,
      protocol: form.protocol.trim() || null,
      proof: form.proof.trim() || null,
    };
    if (kind === "compuesto") {
      payload.components = validComponents.map((c) => ({ resource_id: c.resource_id, quantity: Number(c.quantity) || 1 }));
    } else {
      payload.unit_cost = isTarea && form.unit_cost === "" ? 0 : Number(form.unit_cost || 0);
    }
    try {
      const r = editing
        ? await api.put(`/resources/${resource.resource_id}`, payload)
        : await api.post("/resources", payload);
      onSaved(r.data, editing);
      onOpenChange(false);
      toast.success(editing ? "Recurso actualizado." : "Recurso añadido al catálogo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el recurso.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${kind === "compuesto" ? "sm:max-w-3xl" : "sm:max-w-2xl"} max-h-[92vh] overflow-y-auto`} data-testid="resource-form-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar recurso" : "Nuevo recurso"}</DialogTitle>
          <DialogDescription>Define el recurso para reutilizarlo en cualquier proyecto.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="res-name">Nombre *</Label>
            <Input id="res-name" data-testid="resource-name-input" placeholder="Ej. Cemento gris CEM II"
              value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="res-description">Descripción</Label>
            <Textarea id="res-description" data-testid="resource-description-input" rows={2}
              placeholder="Breve descripción del recurso (opcional)"
              value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          {isTarea && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 space-y-3" data-testid="tarea-fields">
              <div className="space-y-1.5">
                <Label htmlFor="res-protocol">Protocolo (cómo realizarla)</Label>
                <Textarea id="res-protocol" data-testid="resource-protocol-input" rows={4} placeholder="Pasos a seguir, quién, dónde y qué información aportar…" value={form.protocol} onChange={(e) => set("protocol", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="res-proof">Prueba / justificante a obtener</Label>
                <Textarea id="res-proof" data-testid="resource-proof-input" rows={2} placeholder="Ej. resguardo de la solicitud, nº de expediente…" value={form.proof} onChange={(e) => set("proof", e.target.value)} />
              </div>
              <p className="text-[11px] text-muted-foreground">Para una Tarea, el coste y la unidad son opcionales.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Clase de recurso</Label>
            <div className="flex gap-2">
              <Button type="button" variant={kind === "simple" ? "default" : "outline"} className="rounded-full flex-1 gap-2"
                onClick={() => { if (kind === "compuesto") set("unit_cost", ""); setKind("simple"); }} data-testid="resource-kind-simple">
                <Box className="h-4 w-4" /> Simple
              </Button>
              <Button type="button" variant={kind === "compuesto" ? "default" : "outline"} className="rounded-full flex-1 gap-2"
                onClick={() => setKind("compuesto")} data-testid="resource-kind-compuesto">
                <Layers className="h-4 w-4" /> Compuesto
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo de recurso *</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`resource-type-option-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {kind === "simple" ? (
              <div className="space-y-1.5">
                <Label htmlFor="res-cost">Coste ud. compra (€ con IVA) *</Label>
                <Input id="res-cost" data-testid="resource-cost-input" type="number" min="0" step="0.01"
                  placeholder="0,00" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Coste ud. compra (c/IVA, desglose)</Label>
                <div className="h-10 flex items-center rounded-md border border-border bg-background-subtle px-3 font-mono text-sm" data-testid="resource-composite-gross">
                  {formatEuro(grossTotal)}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Tipo de unidad de compra *</Label>
            <Select value={unitMode} onValueChange={setUnitMode}>
              <SelectTrigger data-testid="resource-unit-select"><SelectValue placeholder="Selecciona una unidad" /></SelectTrigger>
              <SelectContent>
                {units.map((u) => (
                  <SelectItem key={u} value={u} data-testid={`resource-unit-option-${u}`}>{u}</SelectItem>
                ))}
                <SelectItem value={NEW_UNIT} data-testid="resource-unit-option-new">+ Nueva unidad…</SelectItem>
              </SelectContent>
            </Select>
            {unitMode === NEW_UNIT && (
              <Input className="mt-2" data-testid="resource-custom-unit-input" placeholder="Escribe la nueva unidad (ej. kg, h, t…)"
                value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} />
            )}
          </div>

          <div className="space-y-1.5">
            <Label>IVA</Label>
            <Select value={vatMode} onValueChange={setVatMode}>
              <SelectTrigger data-testid="resource-vat-select"><SelectValue placeholder="Selecciona el IVA" /></SelectTrigger>
              <SelectContent>
                {vatRates.map((v) => (
                  <SelectItem key={v} value={String(v)} data-testid={`resource-vat-option-${v}`}>{v}%</SelectItem>
                ))}
                <SelectItem value={NEW_VAT} data-testid="resource-vat-option-new">+ Otro IVA…</SelectItem>
              </SelectContent>
            </Select>
            {vatMode === NEW_VAT && (
              <Input className="mt-2" type="number" min="0" max="100" step="0.5" data-testid="resource-custom-vat-input"
                placeholder="Escribe el % de IVA (ej. 4)" value={customVat} onChange={(e) => setCustomVat(e.target.value)} />
            )}
          </div>

          {kind === "compuesto" && (
            <div className="rounded-2xl border-2 border-[#C75B28]/30 bg-[#C75B28]/[0.04] overflow-hidden" data-testid="resource-desglose">
              <div className="flex items-center justify-between gap-3 bg-[#C75B28]/10 px-4 py-3 border-b border-[#C75B28]/20">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-lg bg-[#C75B28]/15 text-[#C75B28] flex items-center justify-center shrink-0">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-tight">Composición del recurso</p>
                    <p className="text-xs text-muted-foreground leading-tight mt-0.5">Busca y añade los recursos simples que lo forman</p>
                  </div>
                </div>
                <Button type="button" size="sm" className="rounded-full gap-1 h-9 bg-[#C75B28] hover:bg-[#C75B28]/90 text-white shrink-0" onClick={addComponent} data-testid="desglose-add">
                  <Plus className="h-3.5 w-3.5" /> Añadir recurso
                </Button>
              </div>
              <div className="p-4 space-y-2.5">
                {components.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-[#C75B28]/30 bg-background/60 py-8 text-center" data-testid="desglose-empty">
                    <p className="text-sm text-muted-foreground">Aún no has añadido recursos al desglose.</p>
                    <p className="text-xs text-muted-foreground mt-1">Pulsa «Añadir recurso» para buscar entre tus recursos simples.</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden sm:flex items-center gap-2 px-1 text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                      <span className="flex-1">Recurso simple</span>
                      <span className="w-24 text-center">Cantidad</span>
                      <span className="w-24 text-right">Subtotal</span>
                      <span className="w-8" />
                    </div>
                    {components.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-xl border border-border bg-card p-2" data-testid={`desglose-row-${i}`}>
                        <Select value={c.resource_id || ""} onValueChange={(v) => updateComponent(i, { resource_id: v })}>
                          <SelectTrigger className="flex-1" data-testid={`desglose-resource-${i}`}><SelectValue placeholder="Buscar recurso simple…" /></SelectTrigger>
                          <SelectContent>
                            {simples.length === 0 && (
                              <div className="px-3 py-2 text-sm text-muted-foreground">No hay recursos simples todavía.</div>
                            )}
                            {simples.map((r) => (
                              <SelectItem key={r.resource_id} value={r.resource_id} data-testid={`desglose-option-${r.resource_id}`}>
                                <div className="flex flex-col py-0.5">
                                  <span>{r.name} · {formatEuro(r.unit_cost)}/{r.unit}</span>
                                  {r.description && (
                                    <span className="text-xs text-muted-foreground line-clamp-2 max-w-[22rem]">{r.description}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input type="number" min="0" step="0.01" className="w-24 text-center" value={c.quantity}
                          onChange={(e) => updateComponent(i, { quantity: e.target.value })} data-testid={`desglose-qty-${i}`} aria-label="Cantidad" />
                        <span className="w-24 text-right font-mono text-sm text-foreground tabular-nums" data-testid={`desglose-subtotal-${i}`}>
                          {formatEuro(grossOf(c.resource_id) * (Number(c.quantity) || 0))}
                        </span>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeComponent(i)} data-testid={`desglose-remove-${i}`} aria-label="Quitar">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center justify-between border-t border-[#C75B28]/20 pt-3 mt-1 px-1">
                      <span className="text-sm text-muted-foreground">Total compra c/IVA</span>
                      <span className="font-mono font-semibold text-base" data-testid="desglose-total">{formatEuro(grossTotal)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="res-safety">% Margen seguridad</Label>
              <Input id="res-safety" data-testid="resource-safety-pct-input" type="number" min="0" step="0.01"
                placeholder="15,00" value={form.safety_pct} onChange={(e) => set("safety_pct", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="res-sale">% Margen venta</Label>
              <Input id="res-sale" data-testid="resource-sale-pct-input" type="number" min="0" step="0.01"
                placeholder="25,00" value={form.sale_pct} onChange={(e) => set("sale_pct", e.target.value)} />
            </div>
          </div>

          {hasCost && (
            <div className="rounded-2xl border border-border bg-background-subtle overflow-hidden" data-testid="resource-cost-breakdown">
              <div className="px-4 py-2.5 bg-secondary/60 border-b border-border">
                <p className="text-sm font-semibold">Desglose de costes (por ud.)</p>
              </div>
              <div className="divide-y divide-border/70 text-sm">
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-muted-foreground">Coste compra ud. (c/IVA)</span>
                  <span className="font-mono tabular-nums" data-testid="cost-gross">{formatEuro(costs.gross)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-muted-foreground">Coste compra ud. neto</span>
                  <span className="font-mono tabular-nums" data-testid="cost-net">{formatEuro(costs.net)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-muted-foreground">Importe del IVA ({vatPct || 0}%)</span>
                  <span className="font-mono tabular-nums" data-testid="cost-vat">{formatEuro(costs.vatAmount)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-muted-foreground">Margen seguridad ({costs.safetyPct || 0}%)</span>
                  <span className="font-mono tabular-nums" data-testid="cost-safety">{formatEuro(costs.safetyAmount)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2 bg-background/60">
                  <span className="font-medium">Coste interno ud.</span>
                  <span className="font-mono font-semibold tabular-nums" data-testid="cost-internal">{formatEuro(costs.internal)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-muted-foreground">Margen venta ({costs.salePct || 0}%)</span>
                  <span className="font-mono tabular-nums" data-testid="cost-sale-margin">{formatEuro(costs.saleAmount)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-[#C75B28]/10">
                  <span className="font-semibold text-[#C75B28]">Precio venta ud.</span>
                  <span className="font-mono font-bold text-[#C75B28] tabular-nums" data-testid="cost-sale-price">{formatEuro(costs.salePrice)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Proveedor / Suministrador</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="resource-supplier-select"><SelectValue placeholder="Sin proveedor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SUPPLIER}>Sin proveedor</SelectItem>
                {(contacts || []).map((c) => (
                  <SelectItem key={c.contact_id} value={c.contact_id} data-testid={`resource-supplier-option-${c.contact_id}`}>{c.company}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!showNewContact && (
              <Button type="button" variant="ghost" size="sm" className="rounded-full text-primary hover:text-primary px-2 h-7 mt-1"
                onClick={() => setShowNewContact(true)} data-testid="resource-supplier-new">
                + Crear contacto nuevo
              </Button>
            )}
            {showNewContact && (
              <div className="mt-2 rounded-xl border border-border bg-background-subtle p-3 space-y-2" data-testid="inline-contact-create">
                <Input data-testid="inline-contact-company" placeholder="Nombre de empresa *"
                  value={newContact.company} onChange={(e) => setNewContact((c) => ({ ...c, company: e.target.value }))} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Persona (opcional)" value={newContact.contact_person} onChange={(e) => setNewContact((c) => ({ ...c, contact_person: e.target.value }))} />
                  <Input placeholder="Teléfono (opcional)" value={newContact.phone} onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))} />
                </div>
                <Input placeholder="Email (opcional)" value={newContact.email} onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))} />
                <div className="flex gap-2">
                  <Button type="button" size="sm" className="rounded-full" onClick={createContact} disabled={creatingContact} data-testid="inline-contact-create-button">
                    {creatingContact ? "Creando…" : "Crear y asignar"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => setShowNewContact(false)}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="res-ref">Referencia</Label>
            <Input id="res-ref" data-testid="resource-reference-input" placeholder="Opcional"
              value={form.reference} onChange={(e) => set("reference", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="res-notes">Notas</Label>
            <Textarea id="res-notes" data-testid="resource-notes-input" placeholder="Opcional" rows={2}
              value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="rounded-full" disabled={saving} data-testid="resource-save-button">
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Añadir recurso"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
